import React from "react";
import { Form, Tooltip } from "antd";
import DataGridConfigurationBuilder from "../../../core/ui/components/grid/DataGridConfigurationBuilder";

import { TAttachmentPresentationModel } from "../../presenter/model/AttachmentPresentationModel";
import TickBox from "../../../core/ui/components/grid/TickBox";
import DataGrid from "../../../core/ui/components/grid/DataGrid";
import AttachmentForm from "./AttachmentForm";
import { ContentTypes } from "../../common/Constants";
import { TAttachmentTypePresentationModel } from "../../../organisations/presenter/model/AttachmentTypePresentationModel";
import { IGenericCodebookItem } from "../../../codebooks/model/GenericCodebookItem";
import { AttachmentType } from "../../../attachmentType/common/Constants";

interface AttachmentTableProps {
  translate: any;
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachments: TAttachmentPresentationModel[];
  createAttachment?: any;
  setFileValue?: any;
  updateAttachment?: any;
  uploadedFile?: any;
  editable: boolean;
  setAttachmentType?: any;
  deleteAttachment?: any;
  contentTypes: IGenericCodebookItem[];
  canDownload?: any;
  organisationId?: number;
  basePath: string;
  anonimisedUploadedFile: any;
  setAnonymisedFileValue: any;
  item: any;
}
const AttachmentTable: React.FC<AttachmentTableProps> = ({
  translate,
  attachments,
  attachmentTypes,
  createAttachment,
  setFileValue,
  uploadedFile,
  updateAttachment,
  editable,
  setAttachmentType,
  deleteAttachment,
  contentTypes,
  canDownload,
  basePath,
  anonimisedUploadedFile,
  setAnonymisedFileValue,
  item
}) => {
  const [CreateForm] = Form.useForm();
  const [UpdateForm] = Form.useForm();
  const builder = DataGridConfigurationBuilder({
    translate,
    resourcePrefix: "editors.attachments"
  });
  const columns = builder.buildColumns(
    "name",
    "description",
    {
      name: "anonymised",
      dataKey: "anonymised",
      sortable: false,
      align: "center",
      render: (record) =>
        record?.attachmentType?.anonymisationRequired && (
          <TickBox value={record.anonymised} />
        )
    },
    {
      name: "content",
      dataKey: "content",
      sortable: false,
      align: "center",
      render: (record: TAttachmentPresentationModel) => {
        let attachmentType = attachmentTypes.find(
          (a) => a.id === record.attachmentTypeId
        );
        let contentType = contentTypes.find(
          (c) => c.id === attachmentType?.attachmentContentTypeId
        );
        if (record.anonymised) {
          if (attachmentType?.anonymisationRequired) {
            return renderDownloadLink(
              record,
              editable,
              canDownload,
              basePath,
              item
            );
          }
          return <></>;
        }
        return contentType?.code === ContentTypes.URL_CONTENT_TYPE ? (
          <Tooltip title={record.content}>
            <a
              href={record.content}
              rel="noopener noreferrer"
              target={"_blank"}
            >
              <i className="fa fa-external-link-alt" aria-hidden="true" />
            </a>
          </Tooltip>
        ) : (!editable || record.identificationCode !== "Y") &&
          record?.attachmentType?.anonymisationRequired ? (
          <a
            href={`/api${basePath}/${item?.id}/attachments/${record.id}/download`}
          >
            <i className="fa fa-download" aria-hidden="true" />
          </a>
        ) : (
          <div></div>
        );
      }
    },
    {
      name: "originalAttachment",
      dataKey: "originalAttachment",
      sortable: false,
      align: "center",
      render: (record: TAttachmentPresentationModel) => {
        return renderDownloadLink(
          record,
          editable,
          canDownload,
          basePath,
          item
        );
      }
    }
  );

  const renderDownloadLink = (
    record: TAttachmentPresentationModel,
    editable,
    canDownload,
    basePath,
    item
  ) => {
    if (record.originalAttachmentId) {
      return record.contentType?.code === ContentTypes.URL_CONTENT_TYPE ? (
        <Tooltip title={record.content}>
          <a
            href={record.originalAttachmentContent}
            rel="noopener noreferrer"
            target={"_blank"}
          >
            <i className="fa fa-external-link-alt" aria-hidden="true" />
          </a>
        </Tooltip>
      ) : (!editable || record.identificationCode !== "Y") &&
        canDownload?.(item?.organisationId) ? (
        <a
          href={`/api${basePath}/${item?.id}/attachments/${record.originalAttachmentId}/download`}
        >
          <i className="fa fa-download" aria-hidden="true" />
        </a>
      ) : (
        <div></div>
      );
    } else {
      return record.contentType?.code === ContentTypes.URL_CONTENT_TYPE ? (
        <Tooltip title={record.content}>
          <a href={record.content} rel="noopener noreferrer" target={"_blank"}>
            <i className="fa fa-external-link-alt" aria-hidden="true" />
          </a>
        </Tooltip>
      ) : !editable || record.identificationCode !== "Y" ? (
        <a
          href={`/api${basePath}/${item?.id}/attachments/${record.id}/download`}
        >
          <i className="fa fa-download" aria-hidden="true" />
        </a>
      ) : (
        <div></div>
      );
    }
  };

  return (
    <DataGrid
      columns={columns}
      translate={translate}
      dataSource={attachments}
      allowCreate={editable}
      allowDelete={editable}
      allowUpdate={editable}
      booleanColumnKeys={["anonymised"]}
      createModalClass={"modal-width"}
      updateModalClass={"modal-width"}
      keyProperty={"id"}
      CreateFormInstance={CreateForm}
      UpdateFormInstance={UpdateForm}
      createForm={
        <AttachmentForm
          translate={translate}
          form={CreateForm}
          attachmentTypes={
            attachmentTypes?.filter(
              (t) => t.name !== AttachmentType.DECISION
            ) || []
          }
          uploadedFile={uploadedFile}
          setFileValue={setFileValue}
          setAttachmentType={setAttachmentType}
          contentTypes={contentTypes}
          anonimisedUploadedFile={anonimisedUploadedFile}
          setAnonymisedFileValue={setAnonymisedFileValue}
        />
      }
      createModalTitle={`${translate(
        "editors.attachments.create_modal_title"
      )}`}
      onCreate={(e) => {
        createAttachment(e);
      }}
      updateForm={
        <AttachmentForm
          translate={translate}
          form={UpdateForm}
          attachmentTypes={attachmentTypes}
          uploadedFile={uploadedFile}
          setFileValue={setFileValue}
          setAttachmentType={setAttachmentType}
          contentTypes={contentTypes}
          anonimisedUploadedFile={anonimisedUploadedFile}
          setAnonymisedFileValue={setAnonymisedFileValue}
        />
      }
      updateModalTitle={`${translate(
        "editors.attachments.update_modal_title"
      )}`}
      onUpdate={(e) => updateAttachment(e)}
      onStartUpdate={(rowData: TAttachmentPresentationModel) => {
        rowData?.contentType?.code !== ContentTypes.URL_CONTENT_TYPE
          ? setFileValue({ name: rowData.name, isDummy: true })
          : {};
        rowData.originalAttachmentId &&
          setAnonymisedFileValue({ name: rowData.name, isDummy: true });
      }}
      deleteModalTitle={translate("editors.attachments.delete_modal_title")}
      deleteModalMessage={translate("editors.attachments.delete_modal_message")}
      onDelete={(e) => deleteAttachment(e)}
    />
  );
};
export default AttachmentTable;
